import { useState, useEffect } from 'react';
import { Save, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';

// ---------------------------------------------------------------------------
// Shared provider metadata — single source of truth for both the tenant
// (AiSettingsSection) and platform (PlatformAiProvidersSection) modals so the
// two lists can't drift. The unified list is the platform set plus openai +
// azure (the two providers tenants additionally need).
// ---------------------------------------------------------------------------

export const PROVIDER_TYPES = [
  { value: 'google', label: 'Google (Gemini)' },
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'openai', label: 'OpenAI (GPT + TTS)' },
  { value: 'deepseek', label: 'DeepSeek' },
  { value: 'groq', label: 'Groq' },
  { value: 'cerebras', label: 'Cerebras' },
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'mistral', label: 'Mistral' },
  { value: 'sambanova', label: 'SambaNova' },
  { value: 'nvidia', label: 'NVIDIA NIM' },
  { value: 'cloudflare', label: 'Cloudflare Workers AI' },
  { value: 'alibaba', label: 'Alibaba Cloud (Qwen / Wan)' },
  { value: 'puter', label: 'Puter (Multi-provider)' },
  { value: 'huggingface', label: 'HuggingFace' },
  { value: 'pollinations', label: 'Pollinations' },
  { value: 'elevenlabs', label: 'ElevenLabs (TTS)' },
  { value: 'azure', label: 'Azure Speech (TTS)' },
  { value: 'deepgram', label: 'Deepgram (STT)' },
  { value: 'speechmatics', label: 'Speechmatics (STT)' },
  { value: 'gladia', label: 'Gladia (STT, async)' },
  { value: 'assemblyai', label: 'AssemblyAI (STT, async)' },
  { value: 'custom', label: 'Custom (OpenAI-compatible)' },
];

// Provider types that need an explicit API Base URL on the row (the form
// surfaces the field for these).
export const REQUIRES_BASE_URL = new Set(['custom']);

// Provider types that need an account id instead of a base URL. RPAAD builds
// every per-capability URL from it — so the admin only enters key + account id
// and auto-discovery does the rest (Cloudflare Workers AI).
export const REQUIRES_ACCOUNT_ID = new Set(['cloudflare']);

export const PROVIDER_META: Record<string, { helpUrl: string; helpLabel: string; requiresKey: boolean }> = {
  google: { helpUrl: 'https://aistudio.google.com/apikey', helpLabel: 'Get API key', requiresKey: true },
  anthropic: { helpUrl: 'https://console.anthropic.com/settings/keys', helpLabel: 'Get API key', requiresKey: true },
  openai: { helpUrl: 'https://platform.openai.com/api-keys', helpLabel: 'Get API key', requiresKey: true },
  deepseek: { helpUrl: 'https://platform.deepseek.com/api_keys', helpLabel: 'Get API key', requiresKey: true },
  huggingface: { helpUrl: 'https://huggingface.co/settings/tokens', helpLabel: 'Get token', requiresKey: true },
  pollinations: { helpUrl: 'https://pollinations.ai', helpLabel: 'Dashboard', requiresKey: false },
  openrouter: { helpUrl: 'https://openrouter.ai/settings/keys', helpLabel: 'Get API key', requiresKey: true },
  cerebras: { helpUrl: 'https://cloud.cerebras.ai', helpLabel: 'Get API key', requiresKey: true },
  mistral: { helpUrl: 'https://console.mistral.ai/api-keys', helpLabel: 'Get API key', requiresKey: true },
  sambanova: { helpUrl: 'https://cloud.sambanova.ai/apis', helpLabel: 'Get API key', requiresKey: true },
  nvidia: { helpUrl: 'https://build.nvidia.com', helpLabel: 'Get API key', requiresKey: true },
  cloudflare: { helpUrl: 'https://dash.cloudflare.com/profile/api-tokens', helpLabel: 'Create token', requiresKey: true },
  // Deep link, because the key page is genuinely hard to find: it is not under
  // any of the console's menus, it is a button pinned to the bottom of the left
  // rail, and the obvious /api-key path 404s.
  alibaba: { helpUrl: 'https://modelstudio.console.alibabacloud.com/ap-southeast-1/model/settings/api-key', helpLabel: 'Get API key', requiresKey: true },
  elevenlabs: { helpUrl: 'https://elevenlabs.io/app/settings/api-keys', helpLabel: 'Get API key', requiresKey: true },
  azure: { helpUrl: 'https://portal.azure.com', helpLabel: 'Azure portal', requiresKey: true },
  deepgram: { helpUrl: 'https://console.deepgram.com', helpLabel: 'Get API key', requiresKey: true },
  speechmatics: { helpUrl: 'https://portal.speechmatics.com', helpLabel: 'Get API key', requiresKey: true },
  gladia: { helpUrl: 'https://app.gladia.io', helpLabel: 'Get API key', requiresKey: true },
  assemblyai: { helpUrl: 'https://www.assemblyai.com/app/api-keys', helpLabel: 'Get API key', requiresKey: true },
  custom: { helpUrl: '', helpLabel: '', requiresKey: false },
};

// Per-capability model slots surfaced in the Advanced override section (and
// echoed read-only in the "Auto-discovered" box when editing).
const MODEL_SLOTS: Array<{ slot: string; label: string; ph: string }> = [
  { slot: 'text', label: 'Text model', ph: 'e.g. meta-llama/llama-3.3-70b-instruct:free' },
  { slot: 'image', label: 'Image model', ph: 'e.g. @cf/black-forest-labs/flux-1-schnell' },
  { slot: 'embedding', label: 'Embedding model', ph: 'e.g. mistral-embed' },
  { slot: 'tts', label: 'TTS model / voice', ph: 'e.g. en-US-Wavenet-D' },
  { slot: 'stt', label: 'STT model', ph: 'e.g. whisper-large-v3-turbo' },
];

// ---------------------------------------------------------------------------
// Types + payload helper
// ---------------------------------------------------------------------------

export interface AiProviderFormValue {
  provider_type: string;
  name: string;
  api_key: string;
  api_base_url: string;
  account_id: string;
  is_active: boolean;
  capabilities: string[];
  models: Record<string, string>;
  /** 'free' | 'paid'. Orders failover: free candidates are tried before paid,
   *  so a scarce/metered key (e.g. a limited Deepgram plan) marked 'paid' is
   *  served only as a last resort. */
  cost_tier: string;
}

/** Minimal shape needed to populate the edit form. Both the tenant `AiProvider`
 *  and platform `PlatformProvider` interfaces satisfy this structurally. */
export interface EditingProvider {
  provider_type: string;
  name: string;
  api_base_url: string | null;
  account_id: string | null;
  has_api_key: boolean;
  capabilities: string[];
  model_config: Record<string, any>;
  is_active: boolean;
  cost_tier?: string;
}

/**
 * Shape the create/update payload shared by the tenant + platform provider
 * endpoints. Capabilities + models are auto-discovered server-side from the
 * provider's /models endpoint; the Advanced section lets the admin pin them
 * when discovery can't (audio providers, Cloudflare image, etc.) and anything
 * left blank still falls back to auto-discovery.
 *
 * Endpoint-specific extras (e.g. the tenant's failover priorities) are merged
 * by the caller.
 */
export function buildAiProviderPayload(
  v: AiProviderFormValue,
  opts: { editing: boolean },
): Record<string, unknown> {
  const cleanedModels = Object.fromEntries(
    Object.entries(v.models).filter(([, val]) => val && val.trim()),
  );
  // Pinning capabilities or model slots via Advanced flips the row to
  // `manual` so the reconcile cron won't overwrite the admin's choices; a bare
  // key (+ account id) stays `auto` and RPAAD maintains it.
  const isOverride = v.capabilities.length > 0 || Object.keys(cleanedModels).length > 0;
  return {
    name: v.name.trim(),
    provider_type: v.provider_type,
    // Create: always send the key (may be empty for free providers).
    // Edit: only send when changed — empty means "keep the current key".
    ...(opts.editing ? (v.api_key ? { api_key: v.api_key } : {}) : { api_key: v.api_key }),
    ...(v.api_base_url ? { api_base_url: v.api_base_url } : {}),
    ...(REQUIRES_ACCOUNT_ID.has(v.provider_type) ? { account_id: v.account_id.trim() } : {}),
    ...(v.capabilities.length > 0 ? { capabilities: v.capabilities } : {}),
    ...(Object.keys(cleanedModels).length > 0 ? { model_config: { models: cleanedModels } } : {}),
    ...(v.cost_tier ? { cost_tier: v.cost_tier } : {}),
    discovery_mode: isOverride ? 'manual' : 'auto',
    is_active: v.is_active,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  isOpen: boolean;
  onClose: () => void;
  editing: EditingProvider | null;
  saving: boolean;
  onSubmit: (value: AiProviderFormValue) => void;
  titleAdd: string;
  titleEdit: string;
}

export default function AiProviderFormModal({
  isOpen,
  onClose,
  editing,
  saving,
  onSubmit,
  titleAdd,
  titleEdit,
}: Props) {
  const [formType, setFormType] = useState('google');
  const [formName, setFormName] = useState('');
  const [formApiKey, setFormApiKey] = useState('');
  const [formApiBaseUrl, setFormApiBaseUrl] = useState('');
  const [formAccountId, setFormAccountId] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [formCostTier, setFormCostTier] = useState('free');
  // Advanced overrides — let the admin pin capabilities + per-capability models
  // when auto-discovery can't. Empty = fall back to auto-discovery.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [formCapabilities, setFormCapabilities] = useState<string[]>([]);
  const [formModels, setFormModels] = useState<Record<string, string>>({});

  // Initialize / reset form state whenever the modal opens (add) or the editing
  // target changes (edit). Replaces the old imperative openAdd/openEdit resets.
  useEffect(() => {
    if (!isOpen) return;
    if (editing) {
      setFormType(editing.provider_type);
      setFormName(editing.name);
      setFormApiKey('');
      setFormApiBaseUrl(editing.api_base_url || '');
      setFormAccountId(editing.account_id || '');
      setFormActive(editing.is_active);
      setFormCostTier(editing.cost_tier || 'free');
      const models = (editing.model_config?.models || {}) as Record<string, string>;
      setFormCapabilities(editing.capabilities || []);
      setFormModels(models);
      // Auto-open the advanced section when the row already carries overrides,
      // so they're visible (and editable) rather than hidden behind the disclosure.
      setShowAdvanced((editing.capabilities?.length || 0) > 0 || Object.keys(models).length > 0);
    } else {
      setFormType('google');
      setFormName(PROVIDER_TYPES.find((p) => p.value === 'google')?.label || 'Google');
      setFormApiKey('');
      setFormApiBaseUrl('');
      setFormAccountId('');
      setFormActive(true);
      setFormCostTier('free');
      setShowAdvanced(false);
      setFormCapabilities([]);
      setFormModels({});
    }
  }, [isOpen, editing]);

  function handleTypeChange(type: string) {
    setFormType(type);
    if (!editing) {
      setFormName(PROVIDER_TYPES.find((p) => p.value === type)?.label || type);
    }
  }

  function toggleFormCapability(cap: string) {
    setFormCapabilities((prev) =>
      prev.includes(cap) ? prev.filter((c) => c !== cap) : [...prev, cap],
    );
  }

  function setFormModel(slot: string, value: string) {
    setFormModels((prev) => ({ ...prev, [slot]: value }));
  }

  function handleSubmit() {
    if (!formName.trim()) return;
    onSubmit({
      provider_type: formType,
      name: formName,
      api_key: formApiKey,
      api_base_url: formApiBaseUrl,
      account_id: formAccountId,
      is_active: formActive,
      capabilities: formCapabilities,
      models: formModels,
      cost_tier: formCostTier,
    });
  }

  const editingModels = (editing?.model_config?.models || {}) as Record<string, string>;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={editing ? titleEdit : titleAdd}>
      <div className="space-y-4">
        <Select
          label="Provider Type"
          value={formType}
          onChange={(e) => handleTypeChange(e.target.value)}
          options={PROVIDER_TYPES}
          disabled={!!editing}
        />

        <Input
          label="Display Name"
          value={formName}
          onChange={(e) => setFormName(e.target.value)}
          placeholder="e.g. Google Gemini"
          required
        />

        {REQUIRES_BASE_URL.has(formType) && (
          <Input
            label="API Base URL"
            value={formApiBaseUrl}
            onChange={(e) => setFormApiBaseUrl(e.target.value)}
            placeholder="https://api.example.com/v1"
            helperText="OpenAI-compatible API base URL (must support /chat/completions)"
            required
          />
        )}

        {REQUIRES_ACCOUNT_ID.has(formType) && (
          <div>
            <Input
              label="Account ID"
              value={formAccountId}
              onChange={(e) => setFormAccountId(e.target.value)}
              placeholder="e.g. 0a1b2c3d4e5f… (Cloudflare account id)"
              required
            />
            <p className="text-[11px] text-gray-500 mt-1">
              RPAAD discovers capabilities + models and builds every per-capability URL from
              your account id — no base URL needed.
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">API Key</label>
          <div className="flex gap-2 items-center">
            <input
              type="password"
              value={formApiKey}
              onChange={(e) => setFormApiKey(e.target.value)}
              placeholder={editing?.has_api_key ? 'Leave empty to keep current key' : (PROVIDER_META[formType]?.requiresKey ? 'Enter API key...' : 'Optional for free models')}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
            />
            {PROVIDER_META[formType]?.helpUrl && (
              <a
                href={PROVIDER_META[formType].helpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-sage-600 hover:underline flex items-center gap-1 whitespace-nowrap"
              >
                {PROVIDER_META[formType].helpLabel}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>

        {editing ? (
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5">
            <p className="text-xs font-medium text-gray-700 mb-1.5">Auto-discovered</p>
            <div className="space-y-1 text-xs text-gray-600">
              <div>
                <span className="text-gray-500">Capabilities: </span>
                {editing.capabilities.length > 0 ? (
                  <span className="font-mono">{editing.capabilities.join(', ')}</span>
                ) : (
                  <span className="text-amber-600">none yet — re-save with key to refresh</span>
                )}
              </div>
              {MODEL_SLOTS.filter(({ slot }) => editingModels[slot]).map(({ slot, label }) => (
                <div key={slot}>
                  <span className="text-gray-500">{label}: </span>
                  <span className="font-mono">{editingModels[slot]}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-500 mt-1.5">
              RPAAD polls this provider hourly and rediscovers when you save with a new API key.
            </p>
          </div>
        ) : (
          <div className="rounded-md border border-purple-100 bg-purple-50 px-3 py-2 text-xs text-purple-800">
            Capabilities and models will be auto-discovered from this provider's <code className="font-mono">/models</code> endpoint when you save.
          </div>
        )}

        <div className="rounded-md border border-gray-200">
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <span>Advanced — override capabilities &amp; models</span>
            {showAdvanced ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {showAdvanced && (
            <div className="px-3 pb-3 pt-1 space-y-3 border-t border-gray-100">
              <p className="text-[11px] text-gray-500">
                Leave blank to auto-discover. Set these for providers discovery can't classify —
                e.g. <span className="font-mono">audio</span> (Groq Whisper STT, Google TTS) or
                Cloudflare image generation.
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Capabilities</label>
                <div className="flex flex-wrap gap-3">
                  {['text', 'image', 'audio'].map((cap) => (
                    <label key={cap} className="flex items-center gap-1.5 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={formCapabilities.includes(cap)}
                        onChange={() => toggleFormCapability(cap)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      {cap}
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {MODEL_SLOTS.map(({ slot, label, ph }) => (
                  <Input
                    key={slot}
                    label={label}
                    value={formModels[slot] || ''}
                    onChange={(e) => setFormModel(slot, e.target.value)}
                    placeholder={ph}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <Select
            label="Cost tier"
            value={formCostTier}
            onChange={(e) => setFormCostTier(e.target.value)}
            options={[
              { value: 'free', label: 'Free — tried first' },
              { value: 'paid', label: 'Paid / metered — last resort' },
            ]}
          />
          <p className="mt-1 text-xs text-gray-500">
            Failover tries free providers before paid ones. Mark a scarce or metered key (e.g. a
            limited Deepgram plan) as paid so it serves only when the free providers fail.
          </p>
        </div>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formActive}
            onChange={(e) => setFormActive(e.target.checked)}
            className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
          />
          <span className="text-sm text-gray-700">Active</span>
        </label>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="button" onClick={handleSubmit} isLoading={saving}>
            <Save className="w-4 h-4 mr-1" />
            {editing ? 'Update' : 'Add Provider'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
